import { createSelector } from '@reduxjs/toolkit';
import { computersNotDeleted } from './computers-not-deleted';
import { personalDataItemsForMap } from './personal-data-items-for-map';
import { personalDataItemsInComputers } from './personal-data-items-in-computers';
import { computerHasMedicalData } from './computer-has-medical-data';
import { alertsForComputer } from './alerts-for-computer';
import { computersInSameNetwork } from './computers-in-same-network';
import { computerIsInNetworkWithServer } from './computer-is-in-network-with-server';
import { computerIsInNetworkWithPersonalData } from './computer-is-in-network-with-personal-data';
import { computerIsInNetworkWithMedicalData } from './computer-is-in-network-with-medical-data';
import { firstBy } from 'thenby';
import { computerUnitTypeInSortOrder } from './computer-unit-type-in-sort-order';
import { computerTypeInSortOrder } from './computer-type-in-sort-order';
import type { PersonalDataItemViewModel } from '@trinnvis/dabih-api-client';

type SortableComputer = {
  name?: string;
  uuid: string;
  type?: string;
  unitType?: string;
  personalDataItems: PersonalDataItemViewModel[];
};

export const computersForMap = createSelector(
  computersNotDeleted,
  personalDataItemsForMap,
  function (computers, personalDataItems) {
    const withPersonalDataItems = computers
      .map((c) => {
        return {
          ...c,
          personalDataItems: personalDataItemsInComputers(personalDataItems, c.uuid),
          alerts: [],
          computerIsInNetworkWithServer: false,
          computerIsInNetworkWithPersonalData: false,
          computerIsInNetworkWithMedicalData: false,
        };
      })
      .map((c) => Object.assign({}, c, { hasMedicalData: computerHasMedicalData(c) }));

    return withPersonalDataItems
      .map((c) => Object.assign({}, c, { alerts: alertsForComputer(c) }))
      .map((c, _i, items) => {
        return {
          ...c,
          computersInSameNetwork: computersInSameNetwork(c, items),
        };
      })
      .map((c) => {
        return {
          ...c,
          computerIsInNetworkWithServer: computerIsInNetworkWithServer(c),
          computerIsInNetworkWithPersonalData: computerIsInNetworkWithPersonalData(c),
          computerIsInNetworkWithMedicalData: computerIsInNetworkWithMedicalData(c),
        };
      })
      .sort(
        firstBy<SortableComputer>(function (v) {
          return computerUnitTypeInSortOrder.indexOf(v.unitType ?? 'UNDEFINED');
        })
          .thenBy(function (v) {
            return computerTypeInSortOrder.indexOf(v.type ?? 'UNDEFINED');
          })
          .thenBy(function (v) {
            return v.personalDataItems.length > 0 ? 0 : 1;
          })
          .thenBy('name', { ignoreCase: true })
          .thenBy('uuid'),
      );
  },
);
